import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { apiService } from '../services/apiService';
import { Submission, Skill, User } from '../types';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Search, BarChart2, TrendingUp } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

export const Reports: React.FC = () => {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState<'progress' | 'search'>('progress');
    const [submissions, setSubmissions] = useState<Submission[]>([]);
    const [skills, setSkills] = useState<Skill[]>([]);
    const [engineers, setEngineers] = useState<User[]>([]);
    const [reviews, setReviews] = useState<any[]>([]);

    // Search State
    const [selectedSkill, setSelectedSkill] = useState('');
    const [minScore, setMinScore] = useState(1);
    const [searchResults, setSearchResults] = useState<any[]>([]);

    // Progress State
    const [selectedEngineer, setSelectedEngineer] = useState('');
    const [progressData, setProgressData] = useState<Submission[]>([]);

    useEffect(() => {
        if (!user) return;

        const loadData = async () => {
            // Get employee hierarchy (self + subordinates)
            const employees = await apiService.getEmployeeHierarchy();
            setEngineers(employees);
            setSelectedEngineer(user.id); // Auto-select self

            // Get submissions for skill search
            // Fetch submissions for all employees in the hierarchy
            const allSubmissionsPromises = employees.map(emp =>
                apiService.getSubmissionsByEmployee(emp.id).catch(() => [])
            );

            const submissionsArrays = await Promise.all(allSubmissionsPromises);
            const allSubmissions = submissionsArrays.flat();

            // Deduplicate by submission ID
            const uniqueSubmissions = allSubmissions.filter((sub, index, self) =>
                index === self.findIndex(s => s.id === sub.id)
            );

            setSubmissions(uniqueSubmissions);
            console.log('Loaded submissions for search:', uniqueSubmissions.length, 'from', employees.length, 'employees');

            // Load skills
            apiService.getSkills().then(setSkills);
        };

        loadData();
    }, [user]);

    const handleSearch = async () => {
        if (!selectedSkill) {
            console.log('No skill selected');
            return;
        }

        console.log('Starting search...', {
            selectedSkill,
            minScore,
            submissionsCount: submissions.length
        });

        // Search through submissions and fetch review data
        const results = await Promise.all(
            submissions.flatMap(async (sub) => {
                const matchingDetails = sub.details.filter(
                    d => d.skillId === selectedSkill && d.selfScore >= minScore
                );

                if (matchingDetails.length === 0) return [];

                // Fetch review for this submission
                const review = await apiService.getReviewBySubmissionId(sub.id);

                return matchingDetails.map(d => {
                    const reviewDetail = review?.details.find((rd: any) => rd.skillId === d.skillId);
                    // Get engineer name from submission or look it up from engineers list
                    const engineerName = sub.engineerName || engineers.find(e => e.id === sub.engineerId)?.name || 'Unknown';

                    return {
                        engineerName: engineerName,
                        engineerId: sub.engineerId,
                        period: sub.period,
                        periodType: sub.periodType,
                        selfScore: d.selfScore,
                        managerScore: reviewDetail?.managerScore || null,
                        managerComment: reviewDetail?.managerComment || null,
                        achievementNotes: d.achievementNotes,
                        submissionStatus: sub.status,
                        reviewStatus: review?.status || 'Not Reviewed'
                    };
                });
            })
        );

        const flatResults = results.flat();
        console.log('Search results:', flatResults.length, flatResults);
        setSearchResults(flatResults);
    };

    const handleProgressFilter = async () => {
        if (!selectedEngineer) return;

        // Fetch submissions for the selected employee
        const employeeSubmissions = await apiService.getSubmissionsByEmployee(selectedEngineer);
        const data = employeeSubmissions.sort((a, b) => a.period.localeCompare(b.period));
        setProgressData(data);

        // Fetch reviews for each submission
        const reviewsData = await Promise.all(
            data.map(sub => apiService.getReviewBySubmissionId(sub.id))
        );
        setReviews(reviewsData.filter(r => r !== null));
    };


    // Prepare chart data for the selected engineer
    const getChartData = () => {
        if (progressData.length === 0) return { barData: [], submittedSkills: [] };

        // Get all unique skills that were submitted
        const submittedSkillIds = new Set<string>();
        progressData.forEach(sub => {
            sub.details.forEach(detail => submittedSkillIds.add(detail.skillId));
        });

        const submittedSkills = skills.filter(s => submittedSkillIds.has(s.id));

        // Get chart data showing skill progression over time with both self and manager scores
        const barData = progressData.map(sub => {
            const dataPoint: any = { period: sub.period };
            const review = reviews.find(r => r && r.submissionId === sub.id);

            sub.details.forEach(detail => {
                const skillName = skills.find(s => s.id === detail.skillId)?.name || 'Unknown';
                dataPoint[`${skillName} (Self)`] = detail.selfScore;

                // Add manager score if review exists
                const reviewDetail = review?.details.find((rd: any) => rd.skillId === detail.skillId);
                if (reviewDetail) {
                    dataPoint[`${skillName} (Manager)`] = reviewDetail.managerScore;
                }
            });
            return dataPoint;
        });

        return { barData, submittedSkills };
    };

    const { barData, submittedSkills } = getChartData();

    // Get all skills from the latest submission for table display
    const getSkillsTableData = () => {
        if (progressData.length === 0) return [];
        const latestSubmission = progressData[progressData.length - 1];
        const latestReview = reviews.find(r => r && r.submissionId === latestSubmission.id);

        return latestSubmission.details.map(detail => {
            const reviewDetail = latestReview?.details.find((rd: any) => rd.skillId === detail.skillId);
            return {
                skillName: skills.find(s => s.id === detail.skillId)?.name || 'Unknown',
                selfScore: detail.selfScore,
                managerScore: reviewDetail?.managerScore || null,
                notes: detail.achievementNotes
            };
        });
    };

    return (
        <div className="space-y-6">
            <h1 className="text-3xl font-bold tracking-tight text-gray-900">Reports & Analytics</h1>

            <div className="flex space-x-4 border-b border-gray-200">
                <button
                    className={`pb-2 px-4 font-medium ${activeTab === 'progress' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500'}`}
                    onClick={() => setActiveTab('progress')}
                >
                    Employee Progress
                </button>
                <button
                    className={`pb-2 px-4 font-medium ${activeTab === 'search' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500'}`}
                    onClick={() => setActiveTab('search')}
                >
                    Skill Search
                </button>
            </div>

            {activeTab === 'progress' && (
                <div className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center"><BarChart2 className="mr-2" /> Employee Progress Report</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="flex gap-4 items-end">
                                <div className="flex-1">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Select Employee</label>
                                    <select
                                        className="w-full rounded-md border border-gray-300 p-2 bg-white text-gray-900"
                                        value={selectedEngineer}
                                        onChange={(e) => setSelectedEngineer(e.target.value)}
                                    >
                                        <option value="">-- Select --</option>
                                        {engineers.map(e => (
                                            <option key={e.id} value={e.id}>{e.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <Button onClick={handleProgressFilter}>Generate Report</Button>
                            </div>
                        </CardContent>
                    </Card>

                    {progressData.length > 0 && (
                        <>
                            {/* Skills Table with Scores */}
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center">
                                        <TrendingUp className="mr-2 h-5 w-5" />
                                        Latest Skills Assessment
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Skill Name</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Self Score</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Manager Score</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Achievement Notes</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {getSkillsTableData().map((skill, idx) => (
                                                    <tr key={idx} className="hover:bg-gray-50">
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{skill.skillName}</td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="flex items-center space-x-2">
                                                                <span className="text-sm font-semibold text-blue-600">{skill.selfScore}/10</span>
                                                                <div className="w-24 bg-gray-200 rounded-full h-2">
                                                                    <div
                                                                        className="bg-blue-600 h-2 rounded-full"
                                                                        style={{ width: `${skill.selfScore * 10}%` }}
                                                                    ></div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            {skill.managerScore !== null ? (
                                                                <div className="flex items-center space-x-2">
                                                                    <span className="text-sm font-semibold text-green-600">{skill.managerScore}/10</span>
                                                                    <div className="w-24 bg-gray-200 rounded-full h-2">
                                                                        <div
                                                                            className="bg-green-600 h-2 rounded-full"
                                                                            style={{ width: `${skill.managerScore * 10}%` }}
                                                                        ></div>
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <span className="text-sm text-gray-400 italic">Not reviewed</span>
                                                            )}
                                                        </td>
                                                        <td className="px-6 py-4 text-sm text-gray-600">{skill.notes}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </CardContent>
                            </Card>


                            {/* Line Chart - Skills Progress Over Time */}
                            <Card>
                                <CardHeader>
                                    <CardTitle>Skills Progress Over Time</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <ResponsiveContainer width="100%" height={400}>
                                        <LineChart data={barData}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                            <XAxis dataKey="period" tick={{ fill: '#6b7280' }} />
                                            <YAxis domain={[0, 10]} tick={{ fill: '#6b7280' }} />
                                            <Tooltip />
                                            <Legend />
                                            {submittedSkills?.flatMap((skill, idx) => [
                                                <Line
                                                    key={`${skill.id}-self`}
                                                    type="monotone"
                                                    dataKey={`${skill.name} (Self)`}
                                                    name={`${skill.name} (Self)`}
                                                    stroke={`hsl(${idx * 60}, 70%, 50%)`}
                                                    strokeWidth={2}
                                                    dot={{ r: 4 }}
                                                />,
                                                <Line
                                                    key={`${skill.id}-manager`}
                                                    type="monotone"
                                                    dataKey={`${skill.name} (Manager)`}
                                                    name={`${skill.name} (Manager)`}
                                                    stroke={`hsl(${idx * 60}, 70%, 50%)`}
                                                    strokeWidth={2}
                                                    strokeDasharray="5 5"
                                                    dot={{ r: 4 }}
                                                />
                                            ])}
                                        </LineChart>
                                    </ResponsiveContainer>
                                </CardContent>
                            </Card>

                            {/* Submission History Table */}
                            <Card>
                                <CardHeader>
                                    <CardTitle>Submission History</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Period</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Skill Count</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Top Skill</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {progressData.map(sub => {
                                                    const topSkill = sub.details.reduce((prev, current) => (prev.selfScore > current.selfScore) ? prev : current, sub.details[0]);
                                                    const skillName = skills.find(s => s.id === topSkill?.skillId)?.name;

                                                    return (
                                                        <tr key={sub.id} className="hover:bg-gray-50">
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{sub.period}</td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{sub.details.length}</td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{skillName || 'N/A'} ({topSkill?.selfScore})</td>
                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${sub.status === 'Submitted' ? 'bg-green-100 text-green-800' :
                                                                    sub.status === 'Draft' ? 'bg-yellow-100 text-yellow-800' :
                                                                        'bg-blue-100 text-blue-800'
                                                                    }`}>
                                                                    {sub.status}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </CardContent>
                            </Card>
                        </>
                    )}
                </div>
            )}

            {activeTab === 'search' && (
                <div className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center"><Search className="mr-2" /> Skill Search</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="grid gap-4 md:grid-cols-3 items-end">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Skill</label>
                                    <select
                                        className="w-full rounded-md border border-gray-300 p-2 bg-white text-gray-900"
                                        value={selectedSkill}
                                        onChange={(e) => setSelectedSkill(e.target.value)}
                                    >
                                        <option value="">-- Select Skill --</option>
                                        {skills.map(s => (
                                            <option key={s.id} value={s.id}>{s.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <Input
                                        label="Min Score"
                                        type="number"
                                        min="1"
                                        max="10"
                                        value={minScore}
                                        onChange={(e) => setMinScore(parseInt(e.target.value))}
                                    />
                                </div>
                                <Button onClick={handleSearch}>Search</Button>
                            </div>
                        </CardContent>
                    </Card>

                    {searchResults.length > 0 && (
                        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            {searchResults.map((res, idx) => (
                                <Card key={idx}>
                                    <CardContent className="pt-6">
                                        <div className="space-y-3">
                                            <div>
                                                <h3 className="font-bold text-lg text-gray-900">{res.engineerName}</h3>
                                                <p className="text-sm text-gray-500">{res.period} ({res.periodType})</p>
                                            </div>

                                            <div className="grid grid-cols-2 gap-3">
                                                <div className="bg-blue-50 p-3 rounded">
                                                    <p className="text-xs text-gray-600 mb-1">Self Score</p>
                                                    <p className="text-2xl font-bold text-blue-600">{res.selfScore}/10</p>
                                                </div>
                                                <div className={`p-3 rounded ${res.managerScore ? 'bg-green-50' : 'bg-gray-50'}`}>
                                                    <p className="text-xs text-gray-600 mb-1">Manager Score</p>
                                                    <p className={`text-2xl font-bold ${res.managerScore ? 'text-green-600' : 'text-gray-400'}`}>
                                                        {res.managerScore ? `${res.managerScore}/10` : 'N/A'}
                                                    </p>
                                                </div>
                                            </div>

                                            <div>
                                                <p className="text-xs font-medium text-gray-700 mb-1">Achievement Notes:</p>
                                                <p className="text-sm text-gray-600 italic bg-gray-50 p-2 rounded">"{res.achievementNotes}"</p>
                                            </div>

                                            {res.managerComment && (
                                                <div>
                                                    <p className="text-xs font-medium text-gray-700 mb-1">Manager Feedback:</p>
                                                    <p className="text-sm text-gray-600 bg-green-50 p-2 rounded">"{res.managerComment}"</p>
                                                </div>
                                            )}

                                            <div className="flex gap-2 pt-2 border-t">
                                                <span className={`px-2 py-1 text-xs font-medium rounded ${res.submissionStatus === 'Reviewed' ? 'bg-green-100 text-green-800' :
                                                    res.submissionStatus === 'Submitted' ? 'bg-blue-100 text-blue-800' :
                                                        'bg-yellow-100 text-yellow-800'
                                                    }`}>
                                                    {res.submissionStatus}
                                                </span>
                                                <span className={`px-2 py-1 text-xs font-medium rounded ${res.reviewStatus === 'Completed' ? 'bg-green-100 text-green-800' :
                                                    res.reviewStatus === 'In Review' ? 'bg-blue-100 text-blue-800' :
                                                        'bg-gray-100 text-gray-600'
                                                    }`}>
                                                    Review: {res.reviewStatus}
                                                </span>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
